import * as trace from 'owa-trace';
import commonStrings from 'owa-strings/owa-mini-common-strings';
import ConversationItem from 'mini-mail-list-store/lib/store/schema/ConversationItem';
import doesConversationContainsMeeting from '../doesConversationContainsMeeting';
import folderNameToId from 'owa-session-store/lib/utils/folderNameToId';
import getCleanPreview from 'mini-mail-list-store/lib/utils/getCleanPreview';
import getConversationIcon, { getMessageIcon } from '../conversationProperty/getMessageClassIcon';
import getHasAttachment from '../getHasAttachment';
import getLastSenderForPersona from '../getLastSenderForPersona';
import getSelectedTableView from 'mini-mail-list-store/lib/utils/getSelectedTableView';
import getSendersOrRecipients from 'mini-mail-list-store/lib/utils/getSendersOrRecipients';
import getTableConversationRelation from 'mini-mail-list-store/lib/utils/getTableConversationRelation';
import getUserConfiguration from 'owa-session-store/lib/actions/getUserConfiguration';
import isItemOfMessageType from 'mini-mail-list-store/lib/utils/isItemOfMessageType';
import Message from 'owa-service/lib/contract/Message';
import ReactListViewType from 'owa-service/lib/contract/ReactListViewType';
import SingleRecipientType from 'owa-service/lib/contract/SingleRecipientType';
import strings from 'owa-strings/owa-mini-strings';
import TableView from 'mini-mail-list-store/lib/store/schema/TableView';
import TableViewConversationRelation from 'mini-mail-list-store/lib/store/schema/TableViewConversationRelation';
import { format } from 'owa-strings/utilities/format';
import { getIsItemSelected } from '../getIsItemSelected';
import { getIsUnflagged } from 'mini-mail-list-store/lib/utils/getTriageState';
import { getListViewStore } from 'mini-mail-list-store/lib/store/Store';
import { getStore as getMailStore } from 'owa-mail-store/lib/store/Store';
import { isMeetingMessage } from 'mini-meeting-invite/lib/utils/isMeetingMessage';
import { MailListItemDataProps } from '../../schema/MailListItemDataProps.types';
import { PropertyIcons } from 'mini-mail-list-item/lib/schema/PropertyIcons';

import {
    getIsPinned,
    getUnreadCount,
} from 'mini-mail-list-store/lib/selectors/mailListDataPropertyGetter';
import {
    isItemInDraftsFolder,
    isItemInSentItemsFolder,
} from 'mini-mail-list-store/lib/utils/isItemInSentOrDraftsFolder';

const getEffectiveSubject = (subject: string, messageClassIcon: PropertyIcons) => {
    // Invoke this only for the conversation view, because for item view the status comes annexed as a part of the subject while being sent by the server itself
    const effectiveSubject = subject || strings.noSubject;
    if (!messageClassIcon) {
        return effectiveSubject;
    }

    switch (messageClassIcon) {
        case PropertyIcons.MailListViewAppointmentAccepted:
            return format(commonStrings.acceptedEventSubjectTemplate, effectiveSubject);
        case PropertyIcons.MailListViewAppointmentTentative:
            return format(commonStrings.tentativeEventSubjectTemplate, effectiveSubject);
        case PropertyIcons.MailListViewAppointmentDeclined:
            return format(commonStrings.declinedEventSubjectTemplate, effectiveSubject);
    }

    return effectiveSubject;
};

/**
 *
 * @param rowId conversationId or ItemId based on the view.
 */
export const getMailListItemDataProps = (rowId: string): MailListItemDataProps => {
    const tableView: TableView = getSelectedTableView();
    const listViewType = tableView.tableQuery.listViewType;

    switch (listViewType) {
        case ReactListViewType.Conversation:
            return getMailListItemPropsForConversation(rowId, tableView);
        case ReactListViewType.Message:
            return getMailListItemPropsForItem(rowId, tableView);

        default:
            trace.trace.warn('Not supported listViewType:' + listViewType);
            return null;
    }
};

const getMailListItemPropsForItem = (
    itemId: string,
    tableView: TableView
): MailListItemDataProps => {
    if (!itemId) {
        trace.errorThatWillCauseAlert('getMailListItemPropsForItem: itemId should be present');
        return null;
    }
    const mailStore = getMailStore();
    const item = mailStore.items.get(itemId);
    if (!item) {
        return null;
    }

    if (!item.ParentFolderId) {
        trace.errorThatWillCauseAlert('getMailListItemPropsForItem: item.ParentFolderId is null.');
    }

    let message;
    if (isItemOfMessageType(item)) {
        message = item as Message;
    } else {
        message = null;
    }

    const userConfiguration = getUserConfiguration();
    const folderId = tableView.tableQuery.folderId;
    const subject = item.Subject ? item.Subject : strings.noSubject;
    const draftsId = folderNameToId('drafts');
    const isDraftsFolder = tableView.tableQuery.folderId === draftsId;
    const isPinned = getIsPinned(itemId, tableView);
    const parentFolderId = item.ParentFolderId && item.ParentFolderId.Id;
    const showDraftsIndicator = item.IsDraft;
    const importance = item.Importance;
    const effectiveMentioned = item.MentionedMe;
    const isFlagged = !getIsUnflagged(itemId);
    const likeCount = message && message.LikeCount ? message.LikeCount : 0;
    const hasAttachment = item.HasAttachments;
    const isSelected = getIsItemSelected(tableView, itemId);
    const lastDeliveryTimestamp = isDraftsFolder ? item.LastModifiedTime : item.DateTimeReceived;
    const messageClassIcon = getMessageIcon(item);
    const from: SingleRecipientType = message && message.From;
    const senderName = from && from.Mailbox ? from.Mailbox.Name : '';
    const recipient = item.DisplayTo;
    const isInSentItemsFolder = isItemInSentItemsFolder(folderId, parentFolderId);
    const isInDraftsFolder = isItemInDraftsFolder(folderId, parentFolderId);
    const sendersOrRecipients = getSendersOrRecipients({
        isInSentItemsFolder,
        isInDraftsFolder,
        uniqueRecipients: [recipient],
        uniqueSenders: [senderName],
    });
    const lastSender = getLastSenderForPersona({
        uniqueRecipients: [recipient],
        isInSentOrDraftsFolder: isInDraftsFolder || isInSentItemsFolder,
        lastSender: from,
    });

    let firstLineText: string;
    let secondLineText: string;
    const unreadCount = getUnreadCount(itemId, tableView);

    if (userConfiguration.UserOptions.ShowSenderOnTopInListView) {
        firstLineText = sendersOrRecipients;
        secondLineText = subject;
    } else {
        secondLineText = sendersOrRecipients;
        firstLineText = subject;
    }

    const thirdLineText = userConfiguration.UserOptions.ShowPreviewTextInListView
        ? getCleanPreview(item.Preview)
        : null;

    return {
        rowId: itemId,
        isPinned,
        showDraftsIndicator,
        parentFolderId,
        firstLineText,
        secondLineText,
        thirdLineText,
        importance,
        effectiveMentioned,
        unreadCount,
        isFlagged,
        likeCount,
        lastSender,
        hasAttachment,
        isSelected,
        lastDeliveryTimestamp,
        messageClassIcon,
        isMeetingMessage: !!(item.ItemClass && isMeetingMessage(item)),
    };
};

const getMailListItemPropsForConversation = (
    conversationId: string,
    tableView: TableView
): MailListItemDataProps => {
    if (!conversationId) {
        trace.errorThatWillCauseAlert(
            'getMailListItemPropsForConversation: conversationId should be present'
        );
        return null;
    }
    const listViewStore = getListViewStore();
    const tableConversationRelation: TableViewConversationRelation = getTableConversationRelation(
        conversationId,
        tableView.id
    );
    const userConfiguration = getUserConfiguration();
    const folderId = tableView.tableQuery.folderId;
    const conversationItem: ConversationItem = listViewStore.conversationItems.get(conversationId);
    const unreadCount = getUnreadCount(conversationId, tableView);
    const isFlagged = !getIsUnflagged(conversationId);
    const isPinned = getIsPinned(conversationId, tableView);
    const isSelected = getIsItemSelected(tableView, conversationItem.id);
    const hasAttachment = getHasAttachment(tableConversationRelation);
    const effectiveMentioned = tableConversationRelation.effectiveMentioned;
    const importance = tableConversationRelation.importance;
    const likeCount = conversationItem.globalLikeCount;
    const lastDeliveryTimestamp = tableConversationRelation.lastDeliveryTimeStamp;
    const parentFolderId = tableConversationRelation.parentFolderId;
    const messageClassIcon: PropertyIcons = getConversationIcon(tableConversationRelation);
    const draftFolderId = folderNameToId('drafts');

    /**
     * Show [Draft] place holder text when:
     * 1) The current item belongs to Drafts folder.
     * 2) The current item belongs to Drafts folder and is in search table view.
     */
    const showDraftsIndicator = draftFolderId === folderId || draftFolderId === parentFolderId;
    let firstLineText;
    let secondLineText;
    const conversationSubject = getEffectiveSubject(conversationItem.subject, messageClassIcon);
    const isInSentItemsFolder = isItemInSentItemsFolder(folderId, parentFolderId);
    const isInDraftsFolder = isItemInDraftsFolder(folderId, parentFolderId);
    const uniqueRecipients = tableConversationRelation.uniqueRecipients;
    // Get senders or recipients
    const sendersOrRecipients = getSendersOrRecipients({
        isInSentItemsFolder,
        uniqueRecipients,
        isInDraftsFolder,
        uniqueSenders: tableConversationRelation.uniqueSenders,
    });
    const lastSender = getLastSenderForPersona({
        uniqueRecipients,
        isInSentOrDraftsFolder: isInDraftsFolder || isInSentItemsFolder,
        lastSender: tableConversationRelation.lastSender,
    });

    if (userConfiguration.UserOptions.ShowSenderOnTopInListView) {
        firstLineText = sendersOrRecipients;
        secondLineText = conversationSubject;
    } else {
        firstLineText = conversationSubject;
        secondLineText = sendersOrRecipients;
    }

    const thirdLineText = userConfiguration.UserOptions.ShowPreviewTextInListView
        ? getCleanPreview(tableConversationRelation.previewText)
        : null;

    return {
        rowId: conversationId,
        effectiveMentioned,
        firstLineText,
        hasAttachment,
        importance,
        isFlagged,
        isPinned,
        isSelected,
        messageClassIcon,
        lastDeliveryTimestamp,
        likeCount,
        parentFolderId,
        secondLineText,
        showDraftsIndicator,
        thirdLineText,
        unreadCount,
        lastSender,
        isMeetingMessage: doesConversationContainsMeeting(tableConversationRelation),
    };
};
